/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-console */
import { ProductName } from '@pypestream/api-services';
import { allLocales, TranslationData } from '@pypestream/translations';
import { css, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';

import {
  BaseElement,
  customElement,
  SlotObserver,
  watch,
} from '../../components/base-element';
import { Translate } from '../../components/base-element/mixins/translation-mixin';
import { getStateChanges, handleGoHome } from '../utils';
import { smartService } from '../xstate/smart.xstate';
import { SmartContext } from '../xstate/smart.xstate-utils';

import '../../components/buttons/icon-button/icon-button.wc';
import '../../components/menu/src/menu-item.wc';
import '../../components/nav/nav-link.wc';
import '../../components/nav/nav-links.wc';
import '../../components/nav/nav-logo.wc';
import '../../components/nav/nav.wc';
import '../nav-breadcrumb/nav-breadcrumb.wc';
import '../universal-user-dropdown/universal-user-dropdown-trigger.wc';
import '../universal-user-dropdown/universal-user-dropdown.wc';

const navTranslations = allLocales.filter(
  (locale) => locale.namespace === 'navigation'
) as unknown as TranslationData[];

/**
 * ### Component Description ###
 * `ps-universal-nav` is a component that provides a navigation bar with support for:
 *  - *nav-breadcrumb*
 *  - *navigation links*
 *  - *additional actions*
 *  - *profile dropdown*
 *
 *
 * ### Features ###
 * The component fetches necessary data (user information, projects, products and their roles) to display information in ***Mega Menu***
 *
 * The component fetches user information and displays the *user's name*, *email*, and *profile photo* in the ***Profile Dropdown***
 */
@customElement('ps-universal-nav')
export class UniversalNavWC extends Translate(
  SlotObserver(BaseElement, ['[slot="nav-breadcrumb-extra"]'])
) {
  static styles = css`
    :host {
      display: contents;
    }
  `;

  context: SmartContext;

  @property({ type: Array }) translations: TranslationData[] | undefined =
    navTranslations;

  @watch('translations')
  onTranslationsChanged() {
    if (this.translations?.length) {
      // use method from translation mixin to add external translations to i18next
      // @ts-ignore
      this.loadTranslations(this.translations);
    }
  }

  /**
   * current ***App name*** (ProductName.Organization | ProductName.AgentAssist | ProductName.Analytics)
   */
  @property({ reflect: true }) app: ProductName = ProductName.Organization;

  @property({ reflect: false, type: Boolean }) isDisabledOrgs?: boolean = false;

  /**
   * current ***Organization id***
   */
  @property({ reflect: false }) orgId: string;

  /**
   * current project
   */
  @property({ reflect: false }) projectId: string;

  /**
   * optional setting to hide the "go back home" link that normally shows up
   */
  @property({ reflect: false, type: Boolean }) hideGoHome?: boolean = false;

  @property({ type: Boolean, reflect: false }) shouldUpdateUser?: boolean =
    false;

  @property() onManagerToolClick?: (route: string) => void;

  // Private properties for debouncing and error handling
  private _initTimeout?: ReturnType<typeof setTimeout>;

  private _isInitializing = false;

  private _subscription?: { unsubscribe: () => void };

  private _hasPendingUserUpdate = false;

  @watch(['shouldUpdateUser', 'onManagerToolClick', 'orgId', 'app'])
  onPropsChanged() {
    if (this.shouldUpdateUser) {
      this._hasPendingUserUpdate = true;
    }

    // Debounce to prevent rapid successive calls
    clearTimeout(this._initTimeout);
    this._initTimeout = setTimeout(() => this.init(), 100);
  }

  private onProjectSelect(selection: string | undefined) {
    this.emit('project-select', { detail: { selection } });
  }

  /**
   * a method that fires when the user switches the current organization
   */
  private onSelectionChange(selection: string | undefined) {
    this.emit('selection-change', { detail: { selection } });
  }

  constructor() {
    super();
    if (!smartService.initialized) {
      try {
        smartService.start();
      } catch (error) {
        console.error('Failed to start smartService:', error);
      }
    }
    this.init = this.init.bind(this);
  }

  async connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();

    try {
      await this.init();
    } catch (error) {
      console.error('UniversalNav initialization failed:', error);
      // @todo - Handle errors gracefully
    }

    // Set up subscription with error handling
    try {
      this._subscription = smartService.subscribe((smartState) => {
        try {
          const diff = getStateChanges<SmartContext>(
            smartState.context,
            this.context
          );

          if (diff) {
            this.context = smartState.context;
          }

          if (
            this.app !== ProductName.Organization &&
            smartState.context.hasAvailableTools === false
          ) {
            try {
              smartService.send({
                type: 'resetHasAvailableTools',
              });
              handleGoHome({ accountId: smartState.context.accountId });
            } catch (error) {
              console.error('Failed to handle navigation:', error);
              // @todo - Handle errors gracefully
            }
          }
        } catch (error) {
          console.error('Error in smartService subscription:', error);
        }
      });
    } catch (error) {
      console.error('Failed to set up smartService subscription:', error);
    }
  }

  disconnectedCallback() {
    // Clean up subscription
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = undefined;
    }

    // Clear any pending timeouts
    if (this._initTimeout) {
      clearTimeout(this._initTimeout);
      this._initTimeout = undefined;
    }

    super.disconnectedCallback();
  }

  async init() {
    if (this._isInitializing) {
      return; // Prevent concurrent initialization
    }

    this._isInitializing = true;

    try {
      try {
        if (this.app !== this.context?.app) {
          smartService.send({
            type: 'changeApp',
            app: this.app,
          });
        }

        if (this.context?.accountId !== this.orgId) {
          smartService.send({
            type: 'changeOrg',
            orgId: this.orgId,
          });
        }

        if (this._hasPendingUserUpdate) {
          smartService.send('loadUser');
          this._hasPendingUserUpdate = false;
        }
      } catch (error) {
        console.error('Error in init state machine operations:', error);
        // @todo - Handle errors gracefully
      }
    } catch (error) {
      console.error('Error in init method:', error);
      throw error; // Re-throw to be handled by caller
    } finally {
      this._isInitializing = false;
    }
  }

  render() {
    return html`
      <ps-nav>
        <ps-nav-breadcrumb
          .app=${this.app}
          .projectId=${this.projectId}
          .hideGoHome=${this.hideGoHome}
          .onProjectSelect=${this.onProjectSelect}
          .onSelectionChange=${this.onSelectionChange}
          .onManagerToolClick=${this.onManagerToolClick}
          ?isDisabledOrgs=${this.isDisabledOrgs}
        >
          ${this.hasSlottedContent('[slot="nav-breadcrumb-extra"]')
            ? html`<ps-icon
                  name="slash"
                  size="xsmall"
                  color="gray"
                  slot="nav-breadcrumb-extra"
                ></ps-icon>
                <slot name="nav-breadcrumb-extra" slot="nav-breadcrumb-extra">
                </slot>`
            : nothing}
        </ps-nav-breadcrumb>
        <slot name="navigation"></slot>
        <slot name="extra"></slot>
        <slot name="controls"></slot>

        <ps-nav-actions>
          <slot name="actions"></slot>
        </ps-nav-actions>

        <slot></slot>
      </ps-nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-universal-nav': UniversalNavWC;
  }
  enum PSElementTagNameMap {
    'ps-universal-nav' = 'ps-universal-nav',
  }
}
